/**
 * ACID8 MIDI Editor web app.
 *
 * Implements the editor protocol described in web/PROTOCOL.md using the
 * browser Web MIDI API. All messages use MIDI channel 16 (index 15 here).
 */

const MIDI_CHANNEL = 15; // zero-based for Web MIDI
const PATTERN_STEPS = 16;
const PATTERN_COUNT = PATTERN_STEPS * 8;
const WAVEFORM_LENGTH = 200;
const NOTE_LABELS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B", "C+"];
const NOTE_COUNT = NOTE_LABELS.length;
const OCTAVE_MAX = 2;
const DEFAULT_QUANTIZE_LEVELS = 16;
const DEFAULT_JITTER_RANGE = 24;
const DEFAULT_MORPH_AMOUNT = 0.5;
const CHAIN_SLOT_COUNT = 5;
const CHAIN_STEP_COUNT = 8;
const CHAIN_SELECT_NOTE = 96;
const CHAIN_REQUEST_NOTE = 106;
const CHAIN_STEP_NOTE_BASE = 118;
const CHAIN_MASK_LOWER_NOTE = 126;
const CHAIN_MASK_UPPER_NOTE = 127;
const FIRMWARE_QUERY_NOTE = 100;
const FIRMWARE_VERSION_MAJOR_NOTE = 101;
const FIRMWARE_VERSION_MINOR_NOTE = 102;
const FIRMWARE_MAGIC_VALUE = 0x51;
const REQUIRED_FIRMWARE_MAJOR = 3;
const REQUIRED_FIRMWARE_MINOR = 0;
const FLAG_CONFIGS = [
  { key: "rest", label: "Rest", type: "toggle" },
  { key: "slide", label: "Slide", type: "toggle" },
  { key: "accent", label: "Accent", type: "toggle" },
  { key: "skip", label: "Skip", type: "toggle" },
];
const PATTERN_MESSAGE_OFFSETS = {
  pitch: 0,
  octave: 16,
  rest: 32,
  slide: 48,
  accent: 64,
  skip: 80,
};
const KICK_SAMPLES = {
  none: null,
  "606": "tool-acid8-606.wav",
  "808": "tool-acid8-808.wav",
  "909": "tool-acid8-909.wav",
};
const DEFAULT_KICK_SELECTION = "none";
const DEFAULT_KICK_VOLUME = 0.8;

const midi = {
  access: null,
  input: null,
  output: null,
};

const patternState = {
  bank: 0,
  slot: 0,
  pitch: Array(PATTERN_STEPS).fill(0),
  octave: Array(PATTERN_STEPS).fill(0),
  rest: Array(PATTERN_STEPS).fill(0),
  slide: Array(PATTERN_STEPS).fill(0),
  accent: Array(PATTERN_STEPS).fill(0),
  skip: Array(PATTERN_STEPS).fill(0),
  swing: 0,
  shape: 0,
  envPitch: 0,
};

const waveformState = {
  target: 0,
  data: Array(WAVEFORM_LENGTH).fill(0),
  receiving: false,
  index: 0,
  lastRequestedTarget: 0,
  targetSynced: false,
};

const chainState = {
  slot: 0,
  steps: Array(CHAIN_STEP_COUNT).fill(0),
  mask: 1,
};

const firmwareStatus = {
  pending: false,
  magic: null,
  major: null,
  minor: null,
  timeoutId: null,
};

let firmwareProbeTimer = null;
let initialDataSyncDone = false;

let patternReceivePending = false;
let chainReceivePending = false;

const elements = {
  statusBadge: document.getElementById("statusBadge"),
  inputSelect: document.getElementById("inputSelect"),
  outputSelect: document.getElementById("outputSelect"),
  refreshBtn: document.getElementById("refreshDevicesBtn"),
  kickSelect: document.getElementById("kickSelect"),
  kickVolume: document.getElementById("kickVolume"),
  chainSlotSelect: document.getElementById("chainSlotSelect"),
  requestChainBtn: document.getElementById("requestChainBtn"),
  chainEditor: document.getElementById("chainEditor"),
  requestPatternBtn: document.getElementById("requestPatternBtn"),
  swingSlider: document.getElementById("swingSlider"),
  envPitchCheckbox: document.getElementById("envPitchCheckbox"),
  patternWrapper: document.getElementById("patternGridWrapper"),
  noteGrid: document.getElementById("noteGrid"),
  slideOverlay: document.getElementById("slideOverlay"),
  flagGrid: document.getElementById("flagGrid"),
  waveformTargetSelect: document.getElementById("waveformTargetSelect"),
  requestWaveformBtn: document.getElementById("requestWaveformBtn"),
  waveformCanvas: document.getElementById("waveformCanvas"),
};

setStatus("No device connected. Choose both MIDI input and output.", "offline");

const noteCells = Array.from({ length: PATTERN_STEPS }, () => Array(NOTE_COUNT).fill(null));
const stepClickTimers = Array(PATTERN_STEPS).fill(null);
const flagCells = {
  rest: Array(PATTERN_STEPS).fill(null),
  slide: Array(PATTERN_STEPS).fill(null),
  accent: Array(PATTERN_STEPS).fill(null),
  skip: Array(PATTERN_STEPS).fill(null),
};
const octaveCells = Array.from({ length: OCTAVE_MAX + 1 }, () => Array(PATTERN_STEPS).fill(null));
const slideSegments = [];
const columnHeaders = Array(PATTERN_STEPS).fill(null);
let playheadStep = null;
let selectedStep = 0;
const kickAudioState = {
  context: null,
  buffers: new Map(),
  loading: new Map(),
  selection: DEFAULT_KICK_SELECTION,
  volume: DEFAULT_KICK_VOLUME,
};
const waveformEditedSteps = new Set();
let chainPatternSelects = [];
let chainActiveToggles = [];
let chainStepCards = [];
const BASE_WAVEFORMS = {
  sine: generateSineWave(WAVEFORM_LENGTH),
  saw: generateSawWave(WAVEFORM_LENGTH),
  triangle: generateTriangleWave(WAVEFORM_LENGTH),
};
const BRUSH_KERNELS = {
  fine: [{ offset: 0, weight: 1 }],
  normal: [
    { offset: 0, weight: 1 },
    { offset: 1, weight: 0.65 },
    { offset: -1, weight: 0.65 },
  ],
  large: [
    { offset: 0, weight: 1 },
    { offset: 1, weight: 0.8 },
    { offset: -1, weight: 0.8 },
    { offset: 2, weight: 0.5 },
    { offset: -2, weight: 0.5 },
  ],
};
const MESSAGE_DELAY_MS = 8; // Small pause between regular MIDI messages
const WAVEFORM_VALUE_DELAY_MS = 16; // Extra breathing room between waveform samples
const WAVEFORM_INDEX_DELAY_MS = 0;
const STEP_DOUBLE_CLICK_DELAY_MS = 250;

const messageQueue = [];
let queueTimerId = null;
let queueActive = false;

let activeBrush = "fine";
let brushButtons = [];
let toolButtons = [];

const slideDragState = {
  active: false,
  startStep: null,
  lastStep: null,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function generateSineWave(length) {
  return Array.from({ length }, (_, i) => {
    const phase = (i / (length - 1)) * Math.PI * 2 - Math.PI / 2;
    return Math.round(((Math.sin(phase) + 1) / 2) * 255);
  });
}

function generateSawWave(length) {
  return Array.from({ length }, (_, i) => Math.round((i / (length - 1)) * 255));
}

function generateTriangleWave(length) {
  return Array.from({ length }, (_, i) => {
    const position = i / (length - 1);
    return position < 0.5
      ? Math.round(position * 2 * 255)
      : Math.round((2 - position * 2) * 255);
  });
}

function formatTime(date = new Date()) {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function appendLog(message) {
  console.log(`[ACID8] ${message}`);
}

function ensureKickAudioContext() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    appendLog("Web Audio API not available. Kick playback disabled.");
    return null;
  }
  if (!kickAudioState.context) {
    kickAudioState.context = new AudioCtx();
  }
  if (kickAudioState.context.state === "suspended") {
    kickAudioState.context.resume().catch((error) => {
      console.error("Failed to resume audio context", error);
    });
  }
  return kickAudioState.context;
}

function decodeAudioBuffer(context, arrayBuffer) {
  return new Promise((resolve, reject) => {
    const response = context.decodeAudioData(arrayBuffer, resolve, reject);
    if (response && typeof response.then === "function") {
      response.then(resolve).catch(reject);
    }
  });
}

function preloadKickSample(key) {
  if (!KICK_SAMPLES[key]) {
    return Promise.resolve(null);
  }
  if (kickAudioState.buffers.has(key)) {
    return Promise.resolve(kickAudioState.buffers.get(key));
  }
  if (kickAudioState.loading.has(key)) {
    return kickAudioState.loading.get(key);
  }

  const ctx = ensureKickAudioContext();
  if (!ctx) {
    return Promise.resolve(null);
  }

  const url = KICK_SAMPLES[key];
  const loader = fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load sample ${url}: ${response.status}`);
      }
      return response.arrayBuffer();
    })
    .then((buffer) => decodeAudioBuffer(ctx, buffer))
    .then((decoded) => {
      kickAudioState.buffers.set(key, decoded);
      kickAudioState.loading.delete(key);
      return decoded;
    })
    .catch((error) => {
      kickAudioState.loading.delete(key);
      console.error(error);
      return null;
    });

  kickAudioState.loading.set(key, loader);
  return loader;
}

async function playKickSample() {
  try {
    const key = kickAudioState.selection;
    if (!key || key === "none") {
      return;
    }

    const volume = clamp(kickAudioState.volume, 0, 1);
    if (volume <= 0) {
      return;
    }

    const ctx = ensureKickAudioContext();
    if (!ctx) {
      return;
    }

    let buffer = kickAudioState.buffers.get(key);
    if (!buffer) {
      buffer = await preloadKickSample(key);
    }

    if (!buffer) {
      return;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gainNode = ctx.createGain();
    gainNode.gain.value = volume;
    source.connect(gainNode).connect(ctx.destination);
    source.start();
  } catch (error) {
    console.error(error);
  }
}

function handleKickBeat(step) {
  if (!Number.isFinite(step) || step < 0) {
    return;
  }
  if (step % 4 !== 0) {
    return;
  }
  if (kickAudioState.selection === "none") {
    return;
  }
  playKickSample();
}

function refreshStepIndicators() {
  for (let step = 0; step < PATTERN_STEPS; step++) {
    const isPlayhead = playheadStep !== null && playheadStep === step;
    const isSelected = selectedStep === step;
    const isWaveEdited = waveformEditedSteps.has(step);
    const header = columnHeaders[step];
    if (header) {
      header.classList.toggle("playhead", isPlayhead);
      header.classList.toggle("selected-step", isSelected);
      header.classList.toggle("waveform-edited", isWaveEdited);
    }
    for (let note = 0; note < NOTE_COUNT; note++) {
      const cell = noteCells[step][note];
      if (!cell) {
        continue;
      }
      cell.classList.toggle("playhead", isPlayhead);
      cell.classList.toggle("selected-step", isSelected);
      cell.classList.remove("waveform-edited");
    }
    for (let octave = 0; octave <= OCTAVE_MAX; octave++) {
      const cell = octaveCells[octave][step];
      if (!cell) {
        continue;
      }
      cell.classList.toggle("playhead", isPlayhead);
      cell.classList.toggle("selected-step", isSelected);
      cell.classList.remove("waveform-edited");
    }
    for (const cells of Object.values(flagCells)) {
      const cell = cells[step];
      if (!cell) {
        continue;
      }
      cell.classList.toggle("playhead", isPlayhead);
      cell.classList.toggle("selected-step", isSelected);
      cell.classList.remove("waveform-edited");
    }
  }
}

function setPlayheadStep(step) {
  if (step === null || Number.isNaN(step) || step < 0 || step >= PATTERN_STEPS) {
    if (playheadStep !== null) {
      playheadStep = null;
      refreshStepIndicators();
    }
    return;
  }

  const normalized = clamp(step, 0, PATTERN_STEPS - 1);
  if (playheadStep === normalized) {
    return;
  }

  playheadStep = normalized;
  refreshStepIndicators();
  handleKickBeat(normalized);
}

function setSelectedStep(step) {
  if (step === null) {
    return;
  }
  const normalized = clamp(Number(step), 0, PATTERN_STEPS - 1);
  if (selectedStep === normalized) {
    return;
  }
  selectedStep = normalized;
  refreshStepIndicators();
}

function markWaveformEdited(step) {
  if (step === null || step < 0 || step >= PATTERN_STEPS) {
    return;
  }
  if (!waveformEditedSteps.has(step)) {
    waveformEditedSteps.add(step);
    refreshStepIndicators();
  }
}

function clearWaveformMarkers() {
  if (waveformEditedSteps.size === 0) {
    return;
  }
  waveformEditedSteps.clear();
  refreshStepIndicators();
}

function setStatus(text, status = "offline") {
  if (!elements.statusBadge) {
    return;
  }
  elements.statusBadge.textContent = text;
  elements.statusBadge.className = `status status-${status}`;
}

function resetFirmwareStatus(pending = false) {
  if (firmwareStatus.timeoutId) {
    clearTimeout(firmwareStatus.timeoutId);
    firmwareStatus.timeoutId = null;
  }
  firmwareStatus.pending = pending;
  firmwareStatus.magic = null;
  firmwareStatus.major = null;
  firmwareStatus.minor = null;
  initialDataSyncDone = false;
}

function scheduleFirmwareProbe() {
  if (firmwareProbeTimer) {
    clearTimeout(firmwareProbeTimer);
  }
  firmwareProbeTimer = window.setTimeout(() => {
    firmwareProbeTimer = null;
    runFirmwareProbe();
  }, 250);
}

function runFirmwareProbe() {
  if (!elements.inputSelect || !elements.outputSelect) {
    return;
  }

  const inputSelected = Boolean(elements.inputSelect.value);
  const outputSelected = Boolean(elements.outputSelect.value);

  if (!inputSelected || !outputSelected) {
    resetFirmwareStatus(false);
    setStatus("No device connected. Choose both MIDI input and output.", "offline");
    return;
  }

  if (!midi.input || !midi.output) {
    resetFirmwareStatus(false);
    setStatus("Waiting for MIDI devices…", "offline");
    return;
  }

  requestFirmwareInfo();
}

function requestFirmwareInfo() {
  if (!midi.input || !midi.output) {
    return;
  }

  resetFirmwareStatus(true);
  setStatus("Checking device…", "offline");
  appendLog("Requesting firmware information from device.");
  sendNoteOff(FIRMWARE_QUERY_NOTE, 0);
  firmwareStatus.timeoutId = window.setTimeout(() => {
    firmwareStatus.timeoutId = null;
    firmwareStatus.pending = false;
    if (firmwareStatus.magic === FIRMWARE_MAGIC_VALUE) {
      return;
    }
    setStatus(
      `Firmware v${REQUIRED_FIRMWARE_MAJOR}.${REQUIRED_FIRMWARE_MINOR} or above required to benefit from all features.`,
      "warning"
    );
  }, 1500);
}

function handleFirmwareMessage(note, velocity) {
  if (note === FIRMWARE_QUERY_NOTE) {
    const v = clamp(velocity, 0, 127);
    if (v === FIRMWARE_MAGIC_VALUE) {
      firmwareStatus.magic = v;
      firmwareStatus.pending = false;
      if (firmwareStatus.timeoutId) {
        clearTimeout(firmwareStatus.timeoutId);
        firmwareStatus.timeoutId = null;
      }
      setStatus("Successfully connected.", "online");
      finalizeFirmwareStatus();
    } else {
      firmwareStatus.magic = null;
      firmwareStatus.pending = false;
      if (firmwareStatus.timeoutId) {
        clearTimeout(firmwareStatus.timeoutId);
        firmwareStatus.timeoutId = null;
      }
      setStatus("Unexpected response from device.", "error");
    }
    return true;
  }

  if (note === FIRMWARE_VERSION_MAJOR_NOTE) {
    firmwareStatus.major = clamp(velocity, 0, 127);
    finalizeFirmwareStatus();
    return true;
  }

  if (note === FIRMWARE_VERSION_MINOR_NOTE) {
    firmwareStatus.minor = clamp(velocity, 0, 127);
    finalizeFirmwareStatus();
    return true;
  }

  return false;
}

function finalizeFirmwareStatus() {
  if (firmwareStatus.magic !== FIRMWARE_MAGIC_VALUE) {
    return;
  }

  if (firmwareStatus.timeoutId) {
    clearTimeout(firmwareStatus.timeoutId);
    firmwareStatus.timeoutId = null;
  }

  if (firmwareStatus.major === null) {
    return;
  }

  firmwareStatus.pending = false;
  const major = firmwareStatus.major;
  const minor = firmwareStatus.minor ?? 0;
  const versionLabel = `${major}.${minor}`;

  if (
    major > REQUIRED_FIRMWARE_MAJOR ||
    (major === REQUIRED_FIRMWARE_MAJOR && minor >= REQUIRED_FIRMWARE_MINOR)
  ) {
    setStatus(`Successfully connected (firmware v${versionLabel}).`, "online");
    triggerInitialDataSync();
  } else {
    setStatus(
      `Connected (firmware v${versionLabel}). Firmware v${REQUIRED_FIRMWARE_MAJOR}.${REQUIRED_FIRMWARE_MINOR} or above required to benefit from all features.`,
      "warning"
    );
  }
}

function triggerInitialDataSync() {
  if (initialDataSyncDone) {
    return;
  }
  initialDataSyncDone = true;

  requestPatternDump();
  requestWaveformDump();
  requestChainDump();
}

function setBrush(brush) {
  if (!BRUSH_KERNELS[brush]) {
    brush = "fine";
  }
  activeBrush = brush;
  brushButtons.forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.brush === brush);
  });
}

function buildPatternEditor() {
  buildNoteGrid();
  setupSlideOverlay();
  buildFlagRows();
  refreshStepIndicators();
}

function clearStepClickTimer(step) {
  if (step < 0 || step >= PATTERN_STEPS) {
    return;
  }
  const timerId = stepClickTimers[step];
  if (timerId) {
    clearTimeout(timerId);
    stepClickTimers[step] = null;
  }
}


function buildNoteGrid() {
  for (let step = 0; step < PATTERN_STEPS; step++) {
    noteCells[step].fill(null);
    clearStepClickTimer(step);
  }
  columnHeaders.fill(null);

  const grid = elements.noteGrid;
  grid.innerHTML = "";

  const corner = document.createElement("div");
  corner.className = "note-grid-cell header corner";
  corner.textContent = "Step";
  grid.append(corner);

  for (let step = 0; step < PATTERN_STEPS; step++) {
    const headerCell = document.createElement("div");
    headerCell.className = "note-grid-cell header";
    headerCell.textContent = String(step + 1).padStart(2, "0");
    headerCell.dataset.step = String(step);
    columnHeaders[step] = headerCell;
    grid.append(headerCell);
  }

  for (let note = NOTE_COUNT - 1; note >= 0; note--) {
    const labelCell = document.createElement("div");
    labelCell.className = "note-grid-cell label";
    labelCell.textContent = NOTE_LABELS[note];
    grid.append(labelCell);

    for (let step = 0; step < PATTERN_STEPS; step++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "note-grid-cell note";
      btn.dataset.step = String(step);
      btn.dataset.note = String(note);
      btn.addEventListener("click", () => {
        setSelectedStep(step);
        if (patternState.pitch[step] === note) {
          if (stepClickTimers[step]) {
            return;
          }
          stepClickTimers[step] = setTimeout(() => {
            stepClickTimers[step] = null;
            toggleRest(step);
          }, STEP_DOUBLE_CLICK_DELAY_MS);
        } else {
          clearStepClickTimer(step);
          setStepPitch(step, note, { broadcast: true });
          if (patternState.rest[step]) {
            setRest(step, 0);
          }
        }
      });
      btn.addEventListener("dblclick", (event) => {
        setSelectedStep(step);
        if (patternState.pitch[step] !== note) {
          return;
        }
        event.preventDefault();
        clearStepClickTimer(step);
        if (patternState.rest[step]) {
          setRest(step, 0);
        }
        toggleAccent(step);
      });
      noteCells[step][note] = btn;
      grid.append(btn);
    }
  }
  refreshStepIndicators();
}

function buildFlagRows() {
  const container = elements.flagGrid;
  container.innerHTML = "";

  const octaveGroup = document.createElement("div");
  octaveGroup.className = "octave-group";

  for (let value = OCTAVE_MAX; value >= 0; value--) {
    octaveCells[value].fill(null);

    const row = document.createElement("div");
    row.className = "octave-row";

    const label = document.createElement("div");
    label.className = "octave-label";
    label.style.paddingLeft = "10px";
    label.textContent = value === 0 ? "0" : `+${value}`;
    row.append(label);

    for (let step = 0; step < PATTERN_STEPS; step++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "octave-cell";
      btn.dataset.step = String(step);
      btn.dataset.value = String(value);
      btn.addEventListener("click", () => {
        setSelectedStep(step);
        patternState.octave[step] = value;
        updateOctaveCell(step);
        updateSlideOverlay();
        sendPatternStepValue("octave", step, patternState.octave[step]);
      });
      octaveCells[value][step] = btn;
      row.append(btn);
    }

    octaveGroup.append(row);
  }

  container.append(octaveGroup);

  // Rest, slide, accent, skip rows
  for (const { key, label, type } of FLAG_CONFIGS) {
    if (key === "octave") {
      continue;
    }

    flagCells[key].fill(null);

    const row = document.createElement("div");
    row.className = "flag-row";

    const labelCell = document.createElement("div");
    labelCell.className = "flag-label";
    labelCell.textContent = label;
    row.append(labelCell);

    for (let step = 0; step < PATTERN_STEPS; step++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `flag-cell ${type}`;
      btn.dataset.step = String(step);
      btn.addEventListener("click", () => {
        setSelectedStep(step);
        if (key === "slide") {
          toggleSlide(step);
        } else if (key === "rest") {
          toggleRest(step);
        } else if (key === "accent") {
          toggleAccent(step);
        } else {
          patternState[key][step] = patternState[key][step] ? 0 : 1;
          updateFlagCell(key, step);
          sendPatternStepValue(key, step, patternState[key][step]);
        }
      });
      flagCells[key][step] = btn;
      row.append(btn);
    }

    container.append(row);
  }
  refreshStepIndicators();
}

function setupSlideOverlay() {
  const svg = elements.slideOverlay;
  const ns = "http://www.w3.org/2000/svg";
  svg.innerHTML = "";
  slideSegments.length = 0;

  for (let step = 0; step < PATTERN_STEPS - 1; step++) {
    const group = document.createElementNS(ns, "g");
    group.dataset.step = String(step);

    const path = document.createElementNS(ns, "path");
    path.classList.add("slide-path");
    path.dataset.step = String(step);
    group.appendChild(path);

    const hit = document.createElementNS(ns, "rect");
    hit.classList.add("slide-hit");
    hit.dataset.step = String(step);
    group.appendChild(hit);

    svg.appendChild(group);
    slideSegments.push({ path, hit });
  }

  updateSlideOverlay();
}

function resolveStepFromElement(element) {
  let node = element;
  while (node && node !== document.body) {
    const raw = node.dataset?.step;
    if (raw !== undefined) {
      const step = Number(raw);
      if (!Number.isNaN(step) && step >= 0 && step < PATTERN_STEPS) {
        return step;
      }
    }
    if (node === elements.patternWrapper) {
      break;
    }
    node = node.parentElement;
  }
  return null;
}

function resolveStepFromPoint(clientX, clientY) {
  const target = document.elementFromPoint(clientX, clientY);
  if (!target) {
    return null;
  }
  return resolveStepFromElement(target);
}

function beginSlideDrag(event) {
  if (event.button !== 0) {
    return;
  }
  const step = resolveStepFromElement(event.target);
  if (step === null) {
    return;
  }

  setSelectedStep(step);
  slideDragState.active = true;
  slideDragState.startStep = step;
  slideDragState.lastStep = step;
}

function trackSlideDrag(event) {
  if (!slideDragState.active) {
    return;
  }
  const step = resolveStepFromPoint(event.clientX, event.clientY);
  if (step === null) {
    return;
  }
  slideDragState.lastStep = step;
}

function completeSlideDrag(event) {
  if (!slideDragState.active) {
    return;
  }

  if (event) {
    trackSlideDrag(event);
  }

  const { startStep, lastStep } = slideDragState;
  slideDragState.active = false;
  slideDragState.startStep = null;
  slideDragState.lastStep = null;

  if (startStep === null || lastStep === null || startStep === lastStep) {
    return;
  }

  setSelectedStep(lastStep);

  let changed = false;

  if (lastStep > startStep) {
    for (let step = startStep; step < lastStep; step++) {
      if (!patternState.slide[step]) {
        patternState.slide[step] = 1;
        updateFlagCell("slide", step);
        sendPatternStepValue("slide", step, 1);
        changed = true;
      }
    }
  } else {
    for (let step = lastStep; step < startStep; step++) {
      if (patternState.slide[step]) {
        patternState.slide[step] = 0;
        updateFlagCell("slide", step);
        sendPatternStepValue("slide", step, 0);
        changed = true;
      }
    }
  }

  // slide overlay refresh already handled by updateFlagCell
}

function cancelSlideDrag() {
  slideDragState.active = false;
  slideDragState.startStep = null;
  slideDragState.lastStep = null;
}

function setStepPitch(step, note, { broadcast = false } = {}) {
  patternState.pitch[step] = clamp(note, 0, NOTE_COUNT - 1);
  updateStepPitchUI(step);
  updateSlideOverlay();
  if (broadcast) {
    setSelectedStep(step);
    sendPatternStepValue("pitch", step, patternState.pitch[step]);
  }
}

function updateStepPitchUI(step) {
  const selected = clamp(patternState.pitch[step], 0, NOTE_COUNT - 1);
  const isRest = !!patternState.rest[step];
  const isAccent = !!patternState.accent[step];
  for (let note = 0; note < NOTE_COUNT; note++) {
    const cell = noteCells[step][note];
    if (!cell) {
      continue;
    }
    cell.classList.toggle("selected", note === selected);
    cell.classList.toggle("resting", isRest && note === selected);
    cell.classList.toggle("accented", isAccent && note === selected && !isRest);
  }
}

function updateFlagCell(flag, step) {
  if (flag === "octave") {
    updateOctaveCell(step);
    return;
  }

  const cell = flagCells[flag][step];
  if (!cell) {
    return;
  }

  const value = patternState[flag][step] ?? 0;
    cell.classList.toggle("active", !!value);

  if (flag === "slide") {
    updateSlideOverlay();
  }

  if (flag === "rest" || flag === "accent") {
    updateStepPitchUI(step);
  }
}

function updatePatternUI() {
  for (let step = 0; step < PATTERN_STEPS; step++) {
    patternState.pitch[step] = clamp(patternState.pitch[step], 0, NOTE_COUNT - 1);
    patternState.octave[step] = clamp(patternState.octave[step], 0, OCTAVE_MAX);
    patternState.rest[step] = patternState.rest[step] ? 1 : 0;
    patternState.slide[step] = patternState.slide[step] ? 1 : 0;
    patternState.accent[step] = patternState.accent[step] ? 1 : 0;
    if (patternState.rest[step] && patternState.accent[step]) {
      patternState.accent[step] = 0;
    }
    patternState.skip[step] = patternState.skip[step] ? 1 : 0;
    updateStepPitchUI(step);
    updateOctaveCell(step);
    updateFlagCell("rest", step);
    updateFlagCell("slide", step);
    updateFlagCell("accent", step);
    updateFlagCell("skip", step);
  }
  elements.swingSlider.value = patternState.swing;
  elements.envPitchCheckbox.checked = !!patternState.envPitch;
  updateSlideOverlay();
  refreshStepIndicators();
}

function toggleSlide(step) {
  patternState.slide[step] = patternState.slide[step] ? 0 : 1;
  updateFlagCell("slide", step);
  sendPatternStepValue("slide", step, patternState.slide[step]);
}

function setRest(step, value) {
  const normalized = value ? 1 : 0;
  if (patternState.rest[step] === normalized) {
    return;
  }
  patternState.rest[step] = normalized;
  updateFlagCell("rest", step);
  sendPatternStepValue("rest", step, patternState.rest[step]);
  if (normalized && patternState.accent[step]) {
    patternState.accent[step] = 0;
    updateFlagCell("accent", step);
    sendPatternStepValue("accent", step, 0);
  }
}

function toggleRest(step) {
  setRest(step, !patternState.rest[step]);
}

function setAccent(step, value) {
  const normalized = value ? 1 : 0;
  if (patternState.accent[step] === normalized) {
    return;
  }
  if (normalized && patternState.rest[step]) {
    patternState.rest[step] = 0;
    updateFlagCell("rest", step);
    sendPatternStepValue("rest", step, 0);
  }
  patternState.accent[step] = normalized;
  updateFlagCell("accent", step);
  sendPatternStepValue("accent", step, patternState.accent[step]);
}

function toggleAccent(step) {
  setAccent(step, !patternState.accent[step]);
}

function getCellCenter(step) {
  const selectedNote = clamp(patternState.pitch[step], 0, NOTE_COUNT - 1);
  const cell = noteCells[step][selectedNote];
  if (!cell) {
    return null;
  }

  const cellRect = cell.getBoundingClientRect();
  const wrapperRect = elements.patternWrapper.getBoundingClientRect();
  return {
    x: cellRect.left - wrapperRect.left + cellRect.width / 2,
    y: cellRect.top - wrapperRect.top + cellRect.height / 2,
  };
}

function updateOctaveCell(step) {
  const value = clamp(patternState.octave[step], 0, OCTAVE_MAX);
  for (let octave = 0; octave <= OCTAVE_MAX; octave++) {
    const cell = octaveCells[octave][step];
    if (!cell) {
      continue;
    }
    cell.classList.toggle("active", octave === value);
  }
}

function updateSlideOverlay() {
  const svg = elements.slideOverlay;
  if (!svg || slideSegments.length === 0) {
    return;
  }

  const wrapperRect = elements.patternWrapper.getBoundingClientRect();
  const width = wrapperRect.width;
  const height = wrapperRect.height;
  svg.setAttribute("width", width);
  svg.setAttribute("height", height);
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);

  for (let step = 0; step < slideSegments.length; step++) {
    const segment = slideSegments[step];
    const start = getCellCenter(step);
    const end = getCellCenter(step + 1);

    if (!start || !end) {
      segment.path.setAttribute("d", "");
      segment.path.classList.remove("active");
      segment.hit.setAttribute("width", 0);
      continue;
    }

    const controlX1 = start.x + (end.x - start.x) * 0.35;
    const controlY1 = start.y;
    const controlX2 = end.x - (end.x - start.x) * 0.35;
    const controlY2 = end.y;

    const pathData = `M ${start.x} ${start.y} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${end.x} ${end.y}`;
    segment.path.setAttribute("d", pathData);
    segment.path.classList.toggle("active", !!patternState.slide[step]);

    const midX = (start.x + end.x) / 2;
    const hitWidth = 44;
    const hitHeight = Math.abs(end.y - start.y) + 56;
    const hitX = midX - hitWidth / 2;
    const hitY = Math.min(start.y, end.y) - 28;
    segment.hit.setAttribute("x", hitX);
    segment.hit.setAttribute("y", hitY);
    segment.hit.setAttribute("width", hitWidth);
    segment.hit.setAttribute("height", hitHeight);
  }
}

function drawWaveform() {
  const canvas = elements.waveformCanvas;
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#080b10";
  ctx.fillRect(0, 0, width, height);

  // grid lines
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, height / 2);
  ctx.lineTo(width, height / 2);
  ctx.stroke();

  ctx.strokeStyle = "#4ecdc4";
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < WAVEFORM_LENGTH; i++) {
    const value = waveformState.data[i] / 255;
    const x = (i / (WAVEFORM_LENGTH - 1)) * width;
    const y = height - value * height;
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

function ensureOutput() {
  if (!midi.output) {
    appendLog("Select a MIDI output first.");
    return false;
  }
  return true;
}

function flushMessageQueue() {
  if (!queueActive) {
    return;
  }

  if (!midi.output) {
    messageQueue.length = 0;
    queueActive = false;
    if (queueTimerId !== null) {
      clearTimeout(queueTimerId);
      queueTimerId = null;
    }
    return;
  }

  const packet = messageQueue.shift();
  if (!packet) {
    queueActive = false;
    if (queueTimerId !== null) {
      clearTimeout(queueTimerId);
      queueTimerId = null;
    }
    return;
  }

  midi.output.send(packet.bytes);

  if (messageQueue.length > 0) {
    const delay = packet.delay ?? MESSAGE_DELAY_MS;
    queueTimerId = setTimeout(flushMessageQueue, Math.max(0, delay));
  } else {
    queueActive = false;
    queueTimerId = null;
  }
}

function clampPatternValue(key, value) {
  switch (key) {
    case "pitch":
      return clamp(value, 0, NOTE_COUNT - 1);
    case "octave":
      return clamp(value, 0, OCTAVE_MAX);
    case "rest":
    case "slide":
    case "accent":
    case "skip":
      return value ? 1 : 0;
    default:
      return value;
  }
}

function sendPatternStepValue(key, step, value) {
  const offset = PATTERN_MESSAGE_OFFSETS[key];
  if (offset === undefined || step < 0 || step >= PATTERN_STEPS) {
    return;
  }
  const note = offset + step;
  const payload = clampPatternValue(key, value);
  sendNoteOff(note, payload);
}

function sendMessage(bytes, delay = MESSAGE_DELAY_MS) {
  if (!ensureOutput()) {
    return;
  }
  messageQueue.push({ bytes, delay });

  if (!queueActive) {
    queueActive = true;
    flushMessageQueue();
  }
}

function sendNoteOff(note, velocity, delay = MESSAGE_DELAY_MS) {
  const status = 0x80 | MIDI_CHANNEL;
  sendMessage([status, note & 0x7f, velocity & 0x7f], delay);
}

function sendWaveIndex(index) {
  if (index <= 127) {
    sendNoteOff(109, index, WAVEFORM_INDEX_DELAY_MS);
  } else {
    sendNoteOff(111, index - 127, WAVEFORM_INDEX_DELAY_MS);
  }
}

function sendWaveValue(value) {
  if (value <= 127) {
    sendNoteOff(112, value, WAVEFORM_VALUE_DELAY_MS);
  } else {
    sendNoteOff(113, value - 127, WAVEFORM_VALUE_DELAY_MS);
  }
}

function ensureWaveTargetSync() {
  if (!waveformState.targetSynced) {
    sendNoteOff(114, waveformState.target);
    waveformState.targetSynced = true;
  }
}

function sendWaveSample(index, value) {
  ensureWaveTargetSync();
  sendWaveIndex(index);
  sendWaveValue(value);
}

function applyWaveformTransform(mutator, description) {
  const buffer = waveformState.data.slice();
  const result = mutator(buffer);
  if (!result) {
    return;
  }

  const current = waveformState.data;
  let sent = 0;

  for (let i = 0; i < WAVEFORM_LENGTH; i++) {
    const nextValue = clamp(Math.round(result[i] ?? current[i]), 0, 255);
    if (current[i] !== nextValue) {
      current[i] = nextValue;
      sendWaveSample(i, nextValue);
      sent++;
    }
  }

  drawWaveform();
  if (sent > 0) {
    markWaveformEdited(selectedStep);
  }
  if (description) {
    appendLog(sent > 0 ? `${description} (${sent} samples sent)` : `${description} (no change)`);
  }
}

function smoothWaveform(data, passes = 1) {
  let working = data.slice();
  for (let pass = 0; pass < passes; pass++) {
    const next = working.slice();
    for (let i = 0; i < working.length; i++) {
      const prev = working[i - 1] ?? working[i];
      const current = working[i];
      const nextVal = working[i + 1] ?? working[i];
      next[i] = (prev + current * 2 + nextVal) / 4;
    }
    working = next;
  }
  return working;
}

function quantizeWaveform(data, levels = DEFAULT_QUANTIZE_LEVELS) {
  const step = 255 / Math.max(levels - 1, 1);
  return data.map((value) => Math.round(Math.round(value / step) * step));
}

function mirrorWaveform(data) {
  return data.slice().reverse();
}

function invertWaveform(data) {
  return data.map((value) => 255 - value);
}

function jitterWaveform(data, range = DEFAULT_JITTER_RANGE) {
  return data.map((value) => clamp(value + Math.round((Math.random() * 2 - 1) * range), 0, 255));
}

function morphWaveform(data, target, amount = DEFAULT_MORPH_AMOUNT) {
  if (!target || target.length !== data.length) {
    return data;
  }
  const blend = 1 - amount;
  return data.map((value, index) => value * blend + target[index] * amount);
}

function applyBrush(centerIndex, value) {
  const kernel = BRUSH_KERNELS[activeBrush] || BRUSH_KERNELS.fine;
  let changed = false;

  for (const { offset, weight } of kernel) {
    const target = centerIndex + offset;
    if (target < 0 || target >= WAVEFORM_LENGTH) {
      continue;
    }

    const existing = waveformState.data[target];
    const blended = Math.round(clamp(existing * (1 - weight) + value * weight, 0, 255));
    if (blended !== existing) {
      waveformState.data[target] = blended;
      sendWaveSample(target, blended);
      changed = true;
    }
  }

  return changed;
}

function handlePatternMessage(note, velocity) {
  if (note === CHAIN_SELECT_NOTE) {
    applyChainSlotFromDevice(velocity);
    return;
  }

  if (note >= CHAIN_STEP_NOTE_BASE && note < CHAIN_STEP_NOTE_BASE + CHAIN_STEP_COUNT) {
    const step = note - CHAIN_STEP_NOTE_BASE;
    applyChainStepFromDevice(step, velocity);
    return;
  }

  if (note === CHAIN_MASK_LOWER_NOTE) {
    applyChainMaskLow(velocity);
    return;
  }

  if (note === CHAIN_MASK_UPPER_NOTE) {
    applyChainMaskHigh(velocity);
    return;
  }

  const v = clamp(velocity, 0, 127);
  if (note < 16) {
    setStepPitch(note, clamp(v, 0, NOTE_COUNT - 1), { broadcast: false });
  } else if (note < 32) {
    const idx = note - 16;
    patternState.octave[idx] = clamp(v, 0, OCTAVE_MAX);
    updateFlagCell("octave", idx);
  } else if (note < 48) {
    const idx = note - 32;
    patternState.rest[idx] = v ? 1 : 0;
    updateFlagCell("rest", idx);
  } else if (note < 64) {
    const idx = note - 48;
    patternState.slide[idx] = v ? 1 : 0;
    updateFlagCell("slide", idx);
  } else if (note < 80) {
    const idx = note - 64;
    patternState.accent[idx] = v ? 1 : 0;
    updateFlagCell("accent", idx);
  } else if (note >= 80 && note < 96) {
    const idx = note - 80;
    if (idx < PATTERN_STEPS) {
      patternState.skip[idx] = v ? 1 : 0;
      updateFlagCell("skip", idx);
    }
  } else if (note === 103) {
    patternState.swing = v;
    elements.swingSlider.value = v;
  } else if (note === 104) {
    patternState.shape = clamp(v, 0, 15);
  } else if (note === 105) {
    patternState.envPitch = v ? 1 : 0;
    elements.envPitchCheckbox.checked = !!patternState.envPitch;
  } else if (note === 117) {
    if (v >= PATTERN_STEPS) {
      setPlayheadStep(null);
    } else {
      setPlayheadStep(v);
    }
  } else if (note === 108) {
    appendLog("Bank change acknowledged by device.");
  }

  if (patternReceivePending && note === 105) {
    patternReceivePending = false;
    appendLog("Pattern data received.");
    clearWaveformMarkers();
  }
}

function startWaveformReceive() {
  waveformState.receiving = true;
  waveformState.index = 0;
  waveformState.lastRequestedTarget = waveformState.target;
  appendLog("Receiving waveform dump…");
}

function handleWaveformMessage(note, velocity) {
  if (!waveformState.receiving) {
    return false;
  }

  if (note !== 97 && note !== 98) {
    return false;
  }

  const base = note === 97 ? 0 : 128;
  const sample = clamp(base + velocity, 0, 255);
  if (waveformState.index < WAVEFORM_LENGTH) {
    waveformState.data[waveformState.index] = sample;
    waveformState.index++;
  }

  if (waveformState.index >= WAVEFORM_LENGTH) {
    waveformState.receiving = false;
    appendLog(`Waveform ${waveformState.lastRequestedTarget} received (${WAVEFORM_LENGTH} samples).`);
    drawWaveform();
  }
  return true;
}

function onMidiMessage(event) {
  const [status, note, velocity] = event.data;
  const messageType = status & 0xf0;
  const channel = status & 0x0f;
  const isNoteOff = messageType === 0x80 || (messageType === 0x90 && velocity === 0);

  if (channel !== MIDI_CHANNEL) {
    return;
  }

  if (note === 81 && isNoteOff) {
    startWaveformReceive();
    return;
  }

  if (isNoteOff && handleFirmwareMessage(note, velocity)) {
    return;
  }

  if (waveformState.receiving && handleWaveformMessage(note, velocity)) {
    return;
  }

  if (isNoteOff) {
    handlePatternMessage(note, velocity);
  }
}

function populateDeviceSelectors() {
  if (!midi.access) {
    return;
  }

  function populate(select, collection) {
    const current = select.value;
    select.innerHTML = "";
    for (const entry of collection.values()) {
      const option = document.createElement("option");
      option.value = entry.id;
      option.textContent = entry.name || entry.manufacturer || entry.id;
      select.append(option);
    }
    if (collection.size > 0) {
      const candidate = current && collection.has(current) ? current : collection.keys().next().value;
      select.value = candidate;
    } else {
      select.value = "";
    }
  }

  populate(elements.inputSelect, midi.access.inputs);
  populate(elements.outputSelect, midi.access.outputs);

  const selectedInput = elements.inputSelect.value || null;
  const selectedOutput = elements.outputSelect.value || null;
  setMidiInput(selectedInput);
  setMidiOutput(selectedOutput);
}

function setMidiInput(id) {
  const nextInput = id ? midi.access?.inputs.get(id) ?? null : null;
  const previousInput = midi.input;
  const previousId = previousInput?.id ?? null;
  const nextId = nextInput?.id ?? null;

  if (previousInput && previousInput !== nextInput) {
    previousInput.onmidimessage = null;
  }

  midi.input = nextInput;

  if (midi.input) {
    midi.input.onmidimessage = onMidiMessage;
  }

  if (previousId !== nextId) {
    if (midi.input) {
      appendLog(`Using MIDI input: ${midi.input.name || midi.input.id}`);
    } else if (previousInput) {
      appendLog("MIDI input cleared.");
    }
  }

  scheduleFirmwareProbe();
}

function setMidiOutput(id) {
  const nextOutput = id ? midi.access?.outputs.get(id) ?? null : null;
  const previousOutput = midi.output;
  const previousId = previousOutput?.id ?? null;
  const nextId = nextOutput?.id ?? null;

  midi.output = nextOutput;

  if (previousId !== nextId) {
    if (midi.output) {
      appendLog(`Using MIDI output: ${midi.output.name || midi.output.id}`);
      requestChainDump();
    } else if (previousOutput) {
      appendLog("MIDI output cleared.");
    }
  }

  scheduleFirmwareProbe();
}

function requestMidiAccess() {
  if (!navigator.requestMIDIAccess) {
    setStatus("Web MIDI not supported", "error");
    appendLog("This browser does not implement the Web MIDI API.");
    return;
  }

  setStatus("Requesting access…", "offline");

  navigator
    .requestMIDIAccess({ sysex: false })
    .then((access) => {
      midi.access = access;
      populateDeviceSelectors();
      setStatus("MIDI ready", "online");
      appendLog("MIDI access granted.");

      midi.access.onstatechange = () => {
        populateDeviceSelectors();
      };
    })
    .catch((error) => {
      console.error(error);
      setStatus("Access denied", "error");
      appendLog("MIDI access denied by user or system.");
    });
}

function formatPatternLabel(index) {
  const bankIndex = Math.floor(index / PATTERN_STEPS);
  const patternIndex = index % PATTERN_STEPS;
  return `B${bankIndex + 1} - P${patternIndex + 1}`;
}

function buildChainEditor() {
  if (!elements.chainEditor) {
    return;
  }

  const fragment = document.createDocumentFragment();
  chainPatternSelects = [];
  chainActiveToggles = [];
  chainStepCards = [];

  for (let step = 0; step < CHAIN_STEP_COUNT; step++) {
    const card = document.createElement("div");
    card.className = "chain-step";

    const title = document.createElement("div");
    title.className = "chain-step-label";
    title.textContent = `Step ${step + 1}`;
    card.append(title);

    const patternSelect = document.createElement("select");
    for (let patternIndex = 0; patternIndex < PATTERN_COUNT; patternIndex++) {
      const option = document.createElement("option");
      option.value = String(patternIndex);
      option.textContent = formatPatternLabel(patternIndex);
      patternSelect.append(option);
    }
    patternSelect.addEventListener("change", () => {
      const value = clamp(Number(patternSelect.value), 0, PATTERN_COUNT - 1);
      handleChainStepChange(step, value);
    });
    chainPatternSelects.push(patternSelect);
    card.append(patternSelect);

    const activeLabel = document.createElement("label");
    activeLabel.className = "chain-active";

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.addEventListener("change", () => {
      handleChainActiveChange(step, checkbox.checked);
    });
    chainActiveToggles.push(checkbox);
    activeLabel.append(checkbox);

    const activeText = document.createElement("span");
    activeText.textContent = "Active";
    activeLabel.append(activeText);

    card.append(activeLabel);
    chainStepCards.push(card);
    fragment.append(card);
  }

  elements.chainEditor.replaceChildren(fragment);
}

function updateChainSlotUI() {
  if (elements.chainSlotSelect) {
    elements.chainSlotSelect.value = String(chainState.slot);
  }
}

function updateChainSlot(slot) {
  const clamped = clamp(slot, 0, CHAIN_SLOT_COUNT - 1);
  chainState.slot = clamped;
  updateChainSlotUI();
}

function updateChainStepUI(step) {
  const select = chainPatternSelects[step];
  if (select) {
    select.value = String(chainState.steps[step]);
  }
}

function ensureChainMaskValid() {
  if ((chainState.mask & 0xff) === 0) {
    chainState.mask = 1;
  }
}

function updateChainActiveUI() {
  for (let step = 0; step < CHAIN_STEP_COUNT; step++) {
    const bit = 1 << step;
    const active = (chainState.mask & bit) !== 0;
    const checkbox = chainActiveToggles[step];
    if (checkbox) {
      checkbox.checked = active;
    }
    const card = chainStepCards[step];
    if (card) {
      card.classList.toggle("inactive", !active);
    }
  }
}

function updateChainUI() {
  updateChainSlotUI();
  for (let step = 0; step < CHAIN_STEP_COUNT; step++) {
    updateChainStepUI(step);
  }
  updateChainActiveUI();
}

function handleChainSlotChange(slot) {
  updateChainSlot(slot);
  chainState.steps.fill(0);
  chainState.mask = 1;
  updateChainUI();
  chainReceivePending = true;
  sendNoteOff(CHAIN_SELECT_NOTE, chainState.slot);
  appendLog(`Chain slot set to ${chainState.slot} (requesting data).`);
}

function applyChainSlotFromDevice(slot) {
  updateChainSlot(slot);
  chainState.steps.fill(0);
  chainState.mask = 1;
  updateChainUI();
  chainReceivePending = true;
}

function sendChainMask(mask) {
  sendNoteOff(CHAIN_MASK_LOWER_NOTE, mask & 0x7f);
  sendNoteOff(CHAIN_MASK_UPPER_NOTE, (mask >> 7) & 0x01);
}

function handleChainStepChange(step, value) {
  const clamped = clamp(value, 0, PATTERN_COUNT - 1);
  if (chainState.steps[step] === clamped) {
    return;
  }
  chainState.steps[step] = clamped;
  updateChainStepUI(step);
  sendNoteOff(CHAIN_STEP_NOTE_BASE + step, clamped);
}

function applyChainStepFromDevice(step, value) {
  const clamped = clamp(value, 0, PATTERN_COUNT - 1);
  chainState.steps[step] = clamped;
  updateChainStepUI(step);
}

function handleChainActiveChange(step, active) {
  const bit = 1 << step;
  let mask = chainState.mask;
  mask = active ? mask | bit : mask & ~bit;
  if ((mask & 0xff) === 0) {
    mask = bit;
  }
  chainState.mask = mask;
  updateChainActiveUI();
  sendChainMask(mask);
}

function applyChainMaskLow(value) {
  const lower = clamp(value, 0, 0x7f);
  chainState.mask = (chainState.mask & 0x80) | lower;
  ensureChainMaskValid();
  updateChainActiveUI();
}

function applyChainMaskHigh(value) {
  const msb = value ? 0x80 : 0x00;
  chainState.mask = (chainState.mask & 0x7f) | msb;
  ensureChainMaskValid();
  updateChainActiveUI();
  if (chainReceivePending) {
    chainReceivePending = false;
    appendLog(`Chain data received for slot ${chainState.slot}.`);
  }
}

function requestChainDump() {
  chainReceivePending = true;
  sendNoteOff(CHAIN_REQUEST_NOTE, chainState.slot);
  appendLog(`Chain dump requested for slot ${chainState.slot}.`);
}

function requestPatternDump() {
  patternReceivePending = true;
  sendNoteOff(116, 0);
  appendLog("Pattern dump requested.");
}

function requestWaveformDump() {
  waveformState.receiving = false;
  waveformState.index = 0;
  sendNoteOff(110, waveformState.target);
  waveformState.targetSynced = true;
  appendLog(`Waveform dump requested for shape ${waveformState.target}.`);
}

function bindUI() {
  buildPatternEditor();
  updatePatternUI();
  drawWaveform();
  buildChainEditor();
  updateChainUI();

  if (elements.kickSelect) {
    elements.kickSelect.value = kickAudioState.selection;
    elements.kickSelect.addEventListener("change", () => {
      const value = elements.kickSelect.value;
      kickAudioState.selection = Object.prototype.hasOwnProperty.call(KICK_SAMPLES, value)
        ? value
        : DEFAULT_KICK_SELECTION;
      if (kickAudioState.selection !== "none") {
        ensureKickAudioContext();
        preloadKickSample(kickAudioState.selection);
      }
    });
  }

  if (elements.kickVolume) {
    const initialVolume = Math.round(kickAudioState.volume * 100);
    elements.kickVolume.value = String(initialVolume);
    const updateVolume = () => {
      const raw = Number(elements.kickVolume.value);
      const clampedValue = clamp(Number.isFinite(raw) ? raw : 0, 0, 100);
      kickAudioState.volume = clampedValue / 100;
      elements.kickVolume.value = String(clampedValue);
    };
    elements.kickVolume.addEventListener("input", () => {
      updateVolume();
      ensureKickAudioContext();
    });
    elements.kickVolume.addEventListener("change", updateVolume);
  }

  if (elements.chainSlotSelect) {
    elements.chainSlotSelect.value = String(chainState.slot);
    elements.chainSlotSelect.addEventListener("change", () => {
      const slot = clamp(Number(elements.chainSlotSelect.value), 0, CHAIN_SLOT_COUNT - 1);
      handleChainSlotChange(slot);
    });
  }

  if (elements.requestChainBtn) {
    elements.requestChainBtn.addEventListener("click", () => {
      requestChainDump();
    });
  }

  elements.refreshBtn.addEventListener("click", populateDeviceSelectors);

  elements.inputSelect.addEventListener("change", () => {
    setMidiInput(elements.inputSelect.value);
  });

  elements.outputSelect.addEventListener("change", () => {
    setMidiOutput(elements.outputSelect.value);
  });

  elements.requestPatternBtn.addEventListener("click", requestPatternDump);

  elements.swingSlider.addEventListener("input", () => {
    patternState.swing = Number(elements.swingSlider.value);
  });

  elements.swingSlider.addEventListener("change", () => {
    sendNoteOff(103, clamp(patternState.swing, 0, 127));
    appendLog(`Swing set to ${patternState.swing}.`);
  });

  elements.envPitchCheckbox.addEventListener("change", () => {
    patternState.envPitch = elements.envPitchCheckbox.checked ? 1 : 0;
    sendNoteOff(105, patternState.envPitch);
    appendLog(`Env pitch ${patternState.envPitch ? "enabled" : "disabled"}.`);
  });

  elements.waveformTargetSelect.addEventListener("change", () => {
    waveformState.target = Number(elements.waveformTargetSelect.value);
    waveformState.targetSynced = false;
    ensureWaveTargetSync();
    appendLog(`Waveform target set to ${waveformState.target}.`);
    requestWaveformDump();
  });

  elements.requestWaveformBtn.addEventListener("click", requestWaveformDump);

  brushButtons = Array.from(document.querySelectorAll(".brush-toggle"));
  brushButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      setBrush(btn.dataset.brush);
    });
  });
  setBrush(activeBrush);

  toolButtons = Array.from(document.querySelectorAll(".tool-button"));
  toolButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const tool = btn.dataset.tool;
      switch (tool) {
        case "smooth":
          applyWaveformTransform((data) => smoothWaveform(data, 2), "Smoothed waveform");
          break;
        case "quantize":
          applyWaveformTransform((data) => quantizeWaveform(data), "Quantized waveform");
          break;
        case "mirror":
          applyWaveformTransform((data) => mirrorWaveform(data), "Mirrored waveform");
          break;
        case "invert":
          applyWaveformTransform((data) => invertWaveform(data), "Inverted waveform");
          break;
        case "randomize":
          applyWaveformTransform((data) => jitterWaveform(data), "Applied jitter");
          break;
        case "morph": {
          const shape = btn.dataset.shape || "sine";
          const target = BASE_WAVEFORMS[shape] || BASE_WAVEFORMS.sine;
          const label = shape.charAt(0).toUpperCase() + shape.slice(1);
          applyWaveformTransform((data) => morphWaveform(data, target), `Morphed with ${label}`);
          break;
        }
        default:
          break;
      }
    });
  });

  elements.patternWrapper.addEventListener("pointerdown", beginSlideDrag);
  window.addEventListener("pointermove", trackSlideDrag);
  window.addEventListener("pointerup", completeSlideDrag);
  window.addEventListener("pointercancel", cancelSlideDrag);

  let drawing = false;
  function handlePointerEvent(evt) {
    const rect = elements.waveformCanvas.getBoundingClientRect();
    const x = clamp(evt.clientX - rect.left, 0, rect.width);
    const y = clamp(evt.clientY - rect.top, 0, rect.height);
    const index = clamp(Math.round((x / rect.width) * (WAVEFORM_LENGTH - 1)), 0, WAVEFORM_LENGTH - 1);
    const value = clamp(Math.round((1 - y / rect.height) * 255), 0, 255);
    const changed = applyBrush(index, value);
    if (changed) {
      markWaveformEdited(selectedStep);
      drawWaveform();
    }
  }

  elements.waveformCanvas.addEventListener("pointerdown", (evt) => {
    drawing = true;
    elements.waveformCanvas.setPointerCapture(evt.pointerId);
    ensureWaveTargetSync();
    handlePointerEvent(evt);
  });

  elements.waveformCanvas.addEventListener("pointermove", (evt) => {
    if (drawing) {
      handlePointerEvent(evt);
    }
  });

  const finishDrawing = (evt) => {
    drawing = false;
    try {
      elements.waveformCanvas.releasePointerCapture(evt.pointerId);
    } catch (_) {
      /* ignored */
    }
  };

  elements.waveformCanvas.addEventListener("pointerup", finishDrawing);
  elements.waveformCanvas.addEventListener("pointerleave", finishDrawing);

  window.addEventListener("resize", () => {
    updateSlideOverlay();
  });
}

function init() {
  bindUI();
  requestMidiAccess();
  appendLog("Ready. Connect your ACID8 and choose the correct ports.");
}

init();
